import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AppButton,
  Box,
  FieldWrapper,
  FormFieldsContainer,
  Heading,
  Inscription,
  Label,
  Option,
  OptionKey,
  Preloader,
  SelectDropdown} from '@common-fe/common-fe';

import { Address } from '@/common/types';
import { SuccessModal } from '@/components/elements';
import Modal from '@/components/elements/AutoBlockedModal/Modal';
import { PaymentMethod, PaymentMethodOwnerType } from '@/modules/employee/employee.types';
import { PaymentMethodLabel } from '@/modules/employee/TransactionDetails/components/PaymentMethodLabel';
import { usePaymentMethodHasPendingMessage } from '@/modules/employee/TransactionDetails/components/PaymentMethodLabel/usePaymentMethodHasPendingMessage';
import { PaymentMethodVerificationStatus } from '@/modules/ReimburseMe/types/payment.types';
import { getFullAddress } from '@/utils';

import { CheckStatus } from '../ChecksList/constants';
import PaymentMethodOption from '../ChecksList/PaymentMethodOption';
import { useGetPaymentMethods } from '../ChecksList/useGetPaymentMethods.query';

import AccountVerificationPendingMessage from './AccountVerificationPendingMessage';

export interface ReissuePaymentModalCheck {
  employeeId?: string;
  paymentMethodId?: string;
  status?: string;
  address?: Address;
}

interface Props {
  isVisible: boolean;
  check?: ReissuePaymentModalCheck;
  onClose: () => void;
  onSubmit: (selectedPaymentMethodId: OptionKey) => void | Promise<void>;
  onSubmitSuccess?: () => void;
  onSuccessClose: () => void;
  successHeader?: string;
  successButtonText?: string;
  wrapperStyle?: React.CSSProperties;
}

const ReissuePaymentModal: React.FC<Props> = ({
  isVisible,
  check,
  onClose,
  onSubmit,
  onSubmitSuccess,
  onSuccessClose,
  successHeader,
  successButtonText,
  wrapperStyle,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccessful, setIsSuccessful] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>();
  const { hasAccountVerificationPendingMessage } = usePaymentMethodHasPendingMessage(paymentMethod, true);
  const { formattedData: paymentMethods, isLoading: areMethodsLoading } = useGetPaymentMethods(
    isVisible ? check?.employeeId : ''
  );
  const ownerType = useMemo(
    () => paymentMethods.find((method) => method.id === check?.paymentMethodId)?.paymentMethodOwner?.ownerType,
    [paymentMethods, check]
  );
  const isProvider = useMemo(() => ownerType === PaymentMethodOwnerType.PROVIDER, [ownerType]);
  const isReissueRequired = useMemo(() => check?.status === CheckStatus.REISSUE_REQUIRED, [check]);
  const filteredPaymentMethods = useMemo(
    () => paymentMethods.filter((method) => method.paymentMethodOwner?.ownerType === ownerType),
    [paymentMethods, ownerType]
  );
  const paymentMethodsOptions = useMemo<Option[]>(
    () =>
      filteredPaymentMethods.map(
        (method) =>
          ({
            key: method.id,
            value: method.id,
            node: <PaymentMethodOption method={method} />,
          }) as Option
      ),
    [filteredPaymentMethods]
  );
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<OptionKey>('');
  const hasDisabledStatus = useMemo(
    () =>
      filteredPaymentMethods?.find((method) => method.id === selectedPaymentMethodId)?.verificationStatus ===
      PaymentMethodVerificationStatus.PENDING_MANUAL,
    [selectedPaymentMethodId, filteredPaymentMethods]
  );
  useEffect(() => {
    if (!filteredPaymentMethods?.length) return;

    if (!selectedPaymentMethodId) {
      const checkedPaymentMethod = filteredPaymentMethods.find(
        (method) => method.id === check?.paymentMethodId
      );
      setPaymentMethod(checkedPaymentMethod);
      setSelectedPaymentMethodId(checkedPaymentMethod?.id);
    }

    if (selectedPaymentMethodId) {
      setPaymentMethod(filteredPaymentMethods.find((method) => method.id === selectedPaymentMethodId));
    }
  }, [filteredPaymentMethods, setPaymentMethod, selectedPaymentMethodId, setSelectedPaymentMethodId, check]);
  const paymentMethodFullAddress = useMemo(() => {
    if (isReissueRequired) {
      return getFullAddress(check?.address);
    }

    return getFullAddress(paymentMethod?.address);
  }, [paymentMethod, isReissueRequired, check]);

  useEffect(() => setSelectedPaymentMethodId(''), [isVisible, setSelectedPaymentMethodId]);

  const handleSuccessClose = useCallback(() => {
    setIsSuccessful(false);
    setSelectedPaymentMethodId('');
    onSuccessClose();
  }, [onSuccessClose]);

  const handleSubmit = useCallback(async () => {
    setIsSubmitting(true);
    try {
      await onSubmit(selectedPaymentMethodId);
      setIsSuccessful(true);
      onSubmitSuccess?.();
    } catch {
      // parent is responsible for surfacing submit errors
    } finally {
      setIsSubmitting(false);
    }
  }, [onSubmit, selectedPaymentMethodId, onSubmitSuccess]);

  if (isSuccessful) {
    return (
      <SuccessModal
        testId="success_reissued_check"
        visible
        header={successHeader || 'Check Was Successfully Reissued'}
        buttonText={successButtonText || 'Go Back To Checks'}
        onSetVisible={handleSuccessClose}
      />
    );
  }

  return (
    <Modal testId="Reissue_check" visible={isVisible} wrapperStyle={wrapperStyle} onSetVisible={onClose}>
      <Heading level="4" fill textAlign="center">
        Reissue Payment
      </Heading>
      <Inscription textAlign="center" lineHeight="22px" size="large" margin={{ top: 'spacing8' }}>
        Reissuing a payment will void the original check prior to issuing the new payment. The new payment
        will be sent in 1-3 business days.
      </Inscription>
      <Box margin={{ vertical: 'spacing24' }} round="moduleRound" pad="spacing24" background="module">
        <FormFieldsContainer
          pad="spacing24"
          background="canvas"
          round="container1Round"
          border={{ color: 'border2' }}
        >
          {isProvider || isReissueRequired ? null : (
            <FieldWrapper direction="row" align="center" justify="between">
              <Box width={{ min: '244px' }}>
                <Label name="payment-method" isNarrow showRequireIcon>
                  Payment method
                </Label>
                <Inscription color="textSecondary" weight={500} lineHeight="18px" size="small">
                  Select an alternative payment method previously created by the employee.
                </Inscription>
              </Box>
              <Box fill pad={{ left: 'spacing12' }}>
                {areMethodsLoading ? (
                  <Preloader />
                ) : (
                  <SelectDropdown
                    name="payment_method"
                    testId="payment_method"
                    id="payment_method"
                    options={paymentMethodsOptions}
                    singleMode
                    onChange={setSelectedPaymentMethodId}
                    value={selectedPaymentMethodId}
                    placeholder="Select payment method"
                  />
                )}
              </Box>
            </FieldWrapper>
          )}
          <FieldWrapper direction="row" align="center" height={{ min: '40px' }}>
            <Box width={{ min: '256px' }}>
              <Box direction="row" margin={{ bottom: 'spacing2' }}>
                <Inscription weight="bold" lineHeight="20px" color="textTitle">
                  {paymentMethodFullAddress ? 'Address' : 'Details'}
                </Inscription>
              </Box>
            </Box>
            <PaymentMethodLabel
              paymentMethod={paymentMethod}
              hasAccountVerificationPendingMessage={hasAccountVerificationPendingMessage}
              paymentMethodAddress={paymentMethodFullAddress}
            />
          </FieldWrapper>
          {hasAccountVerificationPendingMessage ? <AccountVerificationPendingMessage /> : null}
        </FormFieldsContainer>
      </Box>
      <Box direction="row" justify="end">
        <AppButton buttonType="secondary" color="blue" onClick={onClose} width="133px">
          Cancel
        </AppButton>
        <Box margin={{ left: 'spacing12' }}>
          <AppButton
            buttonType="primary"
            width="133px"
            disabled={isSubmitting || areMethodsLoading || hasDisabledStatus}
            onClick={handleSubmit}
          >
            {isSubmitting || areMethodsLoading ? <Preloader /> : 'Reissue'}
          </AppButton>
        </Box>
      </Box>
    </Modal>
  );
};

export default ReissuePaymentModal;
